import { useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useMunicipios } from './useDadosWizard';
import { erroDaRota } from './validacao';
import type { MunicipioLookup } from './types';

interface Props {
  municipioOrigemIndice: number | null;
  municipioDestinoIndice: number | null;
  /** O nome já escolhido: voltar ao passo mostra a escolha, e não um campo vazio. */
  municipioOrigemNome: string;
  municipioDestinoNome: string;
  onOrigem: (m: MunicipioLookup) => void;
  onDestino: (m: MunicipioLookup) => void;
}

interface BuscaProps {
  rotulo: string;
  nomeInicial: string;
  onSelecionar: (m: MunicipioLookup) => void;
}

/**
 * Autocomplete de município, como combobox (0094).
 *
 * As opções eram `div` com clique, que o teclado não alcançava: não dava para
 * emitir sem mouse. Agora as setas percorrem a lista, Enter escolhe e Esc fecha,
 * com `aria-activedescendant` para o leitor de tela.
 *
 * `selecionado` existe para não reabrir a lista depois da escolha: sem ele,
 * escrever o nome escolhido de volta no campo redispara a busca, que devolve
 * o mesmo município e deixa a lista aberta por cima do campo seguinte.
 */
function BuscaMunicipio({ rotulo, nomeInicial, onSelecionar }: BuscaProps) {
  const id = useId();
  const [texto, setTexto] = useState(nomeInicial);
  const [firmado, setFirmado] = useState(nomeInicial !== '');
  const [semAtf, setSemAtf] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const campoRef = useRef<HTMLInputElement>(null);

  // Suprime a busca enquanto há escolha firmada.
  const { resultados, buscando, falhou } = useMunicipios(firmado ? '' : texto);

  // Clique fora fecha a lista — senão ela fica sobre o campo de baixo.
  useEffect(() => {
    function aoClicarFora(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, []);

  useEffect(() => { setAtivo(0); }, [resultados]);

  function escolher(m: MunicipioLookup) {
    setFirmado(true);
    setSemAtf(!m.codIbge);
    setTexto(m.nome);
    setAberto(false);
    onSelecionar(m);
  }

  function limpar() {
    setFirmado(false);
    setSemAtf(false);
    setTexto('');
    setAberto(false);
    campoRef.current?.focus();
  }

  const mostrarLista = aberto && !firmado && resultados.length > 0;
  const idLista = `${id}-lista`;

  function aoTeclar(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!mostrarLista) {
      if (e.key === 'ArrowDown' && resultados.length > 0) { setAberto(true); e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowDown') { setAtivo(a => Math.min(resultados.length - 1, a + 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setAtivo(a => Math.max(0, a - 1)); e.preventDefault(); }
    else if (e.key === 'Enter') { const m = resultados[ativo]; if (m) escolher(m); e.preventDefault(); }
    else if (e.key === 'Escape') { setAberto(false); e.preventDefault(); }
  }

  return (
    <div ref={containerRef} className="co-campo">
      <label className="co-campo-rotulo" htmlFor={`${id}-campo`}>{rotulo}</label>

      <div className="co-busca">
        <input
          ref={campoRef}
          id={`${id}-campo`}
          className="co-entrada"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={mostrarLista}
          aria-controls={idLista}
          aria-activedescendant={mostrarLista ? `${id}-op-${ativo}` : undefined}
          aria-invalid={semAtf || undefined}
          autoComplete="off"
          value={texto}
          onChange={e => { setTexto(e.target.value); setFirmado(false); setSemAtf(false); setAberto(true); }}
          onFocus={() => { if (!firmado) setAberto(true); }}
          onKeyDown={aoTeclar}
          placeholder="Digite o nome do município"
        />
        {firmado && (
          <button type="button" className="co-busca-limpar" onClick={limpar} aria-label={`Trocar ${rotulo.toLowerCase()}`}>
            <X size={16} aria-hidden="true" />
          </button>
        )}
        {mostrarLista && (
          <ul id={idLista} className="co-busca-lista" role="listbox" aria-label={rotulo}>
            {resultados.map((m, i) => (
              <li
                key={m.indice}
                id={`${id}-op-${i}`}
                className="co-busca-opcao"
                role="option"
                aria-selected={i === ativo}
                aria-disabled={!m.codIbge || undefined}
                // `mousedown` e não `click`: o clique tiraria o foco do campo antes.
                onMouseDown={e => { e.preventDefault(); escolher(m); }}
              >
                {m.nome}{!m.codIbge && ' (não disponível para ATF)'}
              </li>
            ))}
          </ul>
        )}
      </div>

      {buscando && !firmado && <span className="co-campo-ajuda" role="status">Buscando…</span>}
      {falhou && !firmado && (
        <span className="co-campo-ajuda" role="alert">Não foi possível buscar agora. Confira a conexão e digite de novo.</span>
      )}
      {semAtf && (
        <span className="co-campo-ajuda" role="alert">Este município não está disponível para ATF. Escolha outro.</span>
      )}
    </div>
  );
}

/** Passo 3 — handoff §2: campos com autocomplete, distância consultada no passo do trajeto. */
export function PassoRota({
  municipioOrigemIndice, municipioDestinoIndice, municipioOrigemNome, municipioDestinoNome, onOrigem, onDestino,
}: Props) {
  const [origemOk, setOrigemOk] = useState(true);
  const [destinoOk, setDestinoOk] = useState(true);

  const erro = erroDaRota({
    municipioOrigemIndice, municipioOrigemAtfOk: origemOk,
    municipioDestinoIndice, municipioDestinoAtfOk: destinoOk,
    cMunIni: municipioOrigemIndice != null ? 'ok' : '', cMunFim: municipioDestinoIndice != null ? 'ok' : '',
  });

  return (
    <div className="co-pilha">
      <BuscaMunicipio rotulo="Origem" nomeInicial={municipioOrigemNome} onSelecionar={m => { setOrigemOk(!!m.codIbge); onOrigem(m); }} />
      <BuscaMunicipio rotulo="Destino" nomeInicial={municipioDestinoNome} onSelecionar={m => { setDestinoOk(!!m.codIbge); onDestino(m); }} />
      {erro && <p className="co-apoio">{erro}</p>}
    </div>
  );
}
